import {
  ContactMessage,
  ExtendedContactMessage,
} from '@mm/state/model/ContactMessageModel';
import {
  LivechatConversationModel,
  LiveChatInboxStatus,
} from '@mm/state/model/LivechatConversationModel';
import { LivechatMessageModel } from '@mm/state/model/LivechatMessageModel';
import { CassandraPagination } from '@mm/state/store-interfaces';
import { groupBy } from 'lodash';
import moment from 'moment';
import { MenuItem } from '../../../common/components/DropdownMenu/DropdownMenu';
import ObjectUtils from '../../../common/helpers/utils/object-utils';

export const getFormattedDate = (time: number | string | undefined) => {
  if (time === undefined || Number.isNaN(time)) return '';

  return getFormattedDateFromMoment(moment(time));
};
export const getFormattedDateFromMoment = (mTime: moment.Moment) => {
  if (!mTime.isValid()) {
    return '';
  }
  if (mTime.isSame(moment(), 'd')) {
    return `Today ${mTime.format('HH:mm')}`;
  }
  if (mTime.isSame(moment().subtract(1, 'd'), 'd')) {
    return `Yesterday ${mTime.format('HH:mm')}`;
  }
  return mTime.format('YYYY-MM-DD HH:mm');
};

export type ConversationSession = {
  id: number;
  sessionFirstMessageAt: number;
  sessionMessages: (LivechatMessageModel | ExtendedContactMessage)[];
};

// TODO: it would probably make more sense to make it a part of the conversationMachine context
// and update on the events that matter instead of relying on React's useEffect.
// But I'm skipping this refactoring for now to deliver value faster.
export const getConversationSessionsFromMessages = (
  messages: LivechatMessageModel[],
  optimisticMessages: ContactMessage[],
  showRecent: boolean,
  showHistorical: boolean,
) => {
  let mergedMessages: (LivechatMessageModel | ExtendedContactMessage)[] = [
    ...messages,
  ];
  if (optimisticMessages.length) {
    if (messages.length === 0) {
      if (showHistorical) {
        mergedMessages = optimisticMessages
          .filter(m => !m.lcMessageId)
          .map(message => ({
            ...message,
            conversationSessionId: 1,
            time: message.time.valueOf().toString(),
          }));
      }
    } else {
      const firstConversation =
        messages[messages.length - 1].conversationSessionId;
      const lastConversation = messages[0].conversationSessionId;
      const oldestMessageTs = new Date(
        Number(messages[messages.length - 1].time),
      );
      const newestTs = new Date(Number(messages[0].time));
      const firstIndex = showHistorical
        ? 0
        : optimisticMessages.findIndex(
            m =>
              m.time >= oldestMessageTs ||
              m.conversationSessionId === firstConversation,
          );
      const lastIndex = showRecent
        ? undefined
        : optimisticMessages.findIndex(
            m =>
              m.time > newestTs && m.conversationSessionId !== lastConversation,
          );
      const filteredOptimisticMessages =
        firstIndex === -1
          ? []
          : optimisticMessages.slice(
              firstIndex,
              lastIndex === -1 ? undefined : lastIndex,
            );
      let offset = mergedMessages.length;
      filteredOptimisticMessages
        .filter(message => {
          if (!showRecent && message.lcMessageId) {
            return false;
          }
          return (
            messages.findIndex(
              m =>
                message.clientMessageId &&
                m.structure?.clientMessageId === message.clientMessageId,
            ) === -1
          );
        })
        .forEach(optimisticMessage => {
          let sessionId = optimisticMessage.conversationSessionId || 0;
          for (let i = offset - 1; i >= 0; i = i - 1) {
            const message = mergedMessages[i];
            const messageTime = Number(message.time);
            const optimisticMessageTime = optimisticMessage.time.valueOf();
            if (messageTime < optimisticMessageTime) {
              offset = offset - 1;
            } else {
              const previous = mergedMessages[i + 1];
              let sessionIdCandidate = message.conversationSessionId;
              if (previous) {
                sessionIdCandidate =
                  optimisticMessageTime - Number(previous.time) <
                  messageTime - optimisticMessageTime
                    ? previous.conversationSessionId
                    : message.conversationSessionId;
              }
              sessionId = Math.max(sessionIdCandidate, sessionId);
              break;
            }
          }
          mergedMessages.splice(offset, 0, {
            ...optimisticMessage,
            conversationSessionId:
              sessionId || messages[0].conversationSessionId,
            time: optimisticMessage.time.valueOf().toString(),
          });
        });
    }
  }
  const messagesBySessionHash = groupBy(
    mergedMessages,
    'conversationSessionId',
  );
  const result: ConversationSession[] = [];
  for (const [key, value] of Object.entries(messagesBySessionHash)) {
    result.push({
      id: Number(key),
      sessionFirstMessageAt: Number(value[value.length - 1].time),
      sessionMessages: value.reverse(),
    });
  }
  return result.sort((a, b) => a.id - b.id);
};

export const remapConversations = (
  parsedData: Record<string, any> | undefined,
) => {
  if (parsedData) {
    const camelCased = ObjectUtils.deepCamelizeKeys(parsedData);
    return camelCased.rows as LivechatConversationModel[];
  }
  return undefined;
};

export type RemapMessagesDataReturn =
  | undefined
  | (CassandraPagination & { data: LivechatMessageModel[] });

export const remapMessages = (
  parsedData: Record<string, any> | undefined,
): RemapMessagesDataReturn => {
  if (parsedData) {
    const camelCased = ObjectUtils.deepCamelizeKeys(parsedData);
    return {
      data: camelCased.data,
      pagination: camelCased.pagination,
    };
  }
  return undefined;
};

export const mutedData = (mutedTill: string | undefined | null) => {
  const mutedTillNumber = mutedTill ? new Date(mutedTill).valueOf() : undefined;

  const mutedSecondsRemaining = mutedTillNumber
    ? (mutedTillNumber - Date.now()) / 1000
    : undefined;

  return {
    mutedTillNumber,
    mutedSecondsRemaining,
  };
};

export const buildConversationStatusMenuItems = (
  currentInboxStatus: LiveChatInboxStatus,
  setStatus: (updatedStatus: LiveChatInboxStatus) => void,
  shortNames?: boolean,
) => {
  const allItems: (MenuItem & { inboxStatus: LiveChatInboxStatus })[] = [
    {
      name: 'Pending',
      onClick: () => setStatus('pending'),
      inboxStatus: 'pending',
    },
    {
      name: 'Resolved',
      onClick: () => setStatus('resolved'),
      inboxStatus: 'resolved',
    },
    {
      name: 'Done',
      onClick: () => setStatus('done'),
      inboxStatus: 'done',
    },
    {
      name: 'Main',
      onClick: () => setStatus('main'),
      inboxStatus: 'main',
    },
  ];
  return allItems
    .filter(i => i.inboxStatus !== currentInboxStatus)
    .map(item => ({
      ...item,
      name: shortNames ? item.name : `Move to ${item.name}`,
    }));
};
